import { useEffect } from 'react';

interface ArticleSEO {
  publishedTime?: string;
  modifiedTime?: string;
  authorName?: string;
  section?: string;
  tags?: string[];
}

interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string;
  path: string;
  type?: 'website' | 'article';
  article?: ArticleSEO;
}

function buildJsonLd(props: SEOHeadProps): Record<string, unknown> {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://mulebuyspreadsheet.run';
  const pageUrl = `${siteUrl}${props.path}`;

  if (props.type === 'article' && props.article) {
    return {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: props.title,
      description: props.description,
      url: pageUrl,
      datePublished: props.article.publishedTime || '2026-06-01T00:00:00+00:00',
      dateModified: props.article.modifiedTime || '2026-06-22T00:00:00+00:00',
      author: {
        '@type': 'Organization',
        name: props.article.authorName || 'Mulebuy Spreadsheet',
      },
      publisher: {
        '@type': 'Organization',
        name: 'Mulebuy Spreadsheet',
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': pageUrl,
      },
      keywords: props.keywords,
      articleSection: props.article.section || 'Shopping Guide',
    };
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Mulebuy Spreadsheet',
    url: siteUrl,
    description: props.description,
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${siteUrl}?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };
}

export default function SEOHead(props: SEOHeadProps) {
  useEffect(() => {
    document.title = props.title;

    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://mulebuyspreadsheet.run';
    const pageUrl = `${siteUrl}${props.path}`;

    const setMeta = (name: string, content: string, propName?: string) => {
      let el: HTMLMetaElement | null;
      if (propName) {
        el = document.querySelector(`meta[property="${propName}"]`);
      } else {
        el = document.querySelector(`meta[name="${name}"]`);
      }
      if (!el) {
        el = document.createElement('meta');
        if (propName) {
          el.setAttribute('property', propName);
        } else {
          el.setAttribute('name', name);
        }
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', props.description);
    if (props.keywords) {
      setMeta('keywords', props.keywords);
    }

    const ogType = props.type === 'article' ? 'article' : 'website';
    setMeta('og:title', props.title, 'og:title');
    setMeta('og:description', props.description, 'og:description');
    setMeta('og:url', pageUrl, 'og:url');
    setMeta('og:type', ogType, 'og:type');
    setMeta('og:locale', 'en_US', 'og:locale');
    setMeta('twitter:card', 'summary_large_image', 'twitter:card');
    setMeta('twitter:title', props.title, 'twitter:title');
    setMeta('twitter:description', props.description, 'twitter:description');

    const canonicalEl = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (canonicalEl) {
      canonicalEl.setAttribute('href', pageUrl);
    } else {
      const link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      link.setAttribute('href', pageUrl);
      document.head.appendChild(link);
    }

    const jsonLd = buildJsonLd(props);
    const scriptId = 'seo-jsonld';
    let scriptEl = document.getElementById(scriptId) as HTMLScriptElement | null;
    if (!scriptEl) {
      scriptEl = document.createElement('script');
      scriptEl.id = scriptId;
      scriptEl.type = 'application/ld+json';
      document.head.appendChild(scriptEl);
    }
    scriptEl.textContent = JSON.stringify(jsonLd);
  }, [props.title, props.description, props.keywords, props.path, props.type, props.article]);

  return null;
}